/**
 * PoolAwarenessPanel — Phase 3 pre-flight diagnostic.
 *
 * Two surfaces:
 *   1. **Pool-check** (existing, May 17) — per-rule candidate-count banner.
 *      Renders red flags on rules that produce zero candidates.
 *   2. **Full diagnostic** (Feb 2026, §16 day-0 gate) — per-rule predicate
 *      decomposition with bottleneck identification + suggested fix. Hit
 *      via "Run full diagnostic" button. Downloads markdown for paste into
 *      spec §16.5.
 *
 * Both are read-only, admin-only, and never customer-facing.
 */
import React, { useCallback, useState } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  RotateCw,
  FlaskConical,
  Stethoscope,
  Download,
  Tag,
  Send,
} from "lucide-react";

const _API_BASE =
  typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL ||
      (typeof window !== "undefined" ? window.location.origin : "");
const API = `${_API_BASE}/api`;

function authedFetch(url, opts = {}) {
  return fetch(url, { credentials: "include", ...opts });
}

const DEFAULT_WS = "a24c8722-c708-4511-a5cc-8b98a884bbfb";

function TriggerCell({ trigger }) {
  if (!trigger) return <span className="text-slate-400">—</span>;
  const parts = [];
  for (const [k, v] of Object.entries(trigger)) {
    let display = Array.isArray(v) ? v.join(", ") : String(v);
    if (display.length > 50) display = display.slice(0, 47) + "…";
    parts.push(
      <span key={k} className="font-mono text-[10px] text-slate-600 mr-2">
        {k}={display}
      </span>,
    );
  }
  return <div className="flex flex-wrap gap-y-0.5">{parts}</div>;
}

function DiagnosticRuleBlock({ rule }) {
  if (rule.card_driven) {
    return (
      <div
        className="border border-slate-200 rounded p-3 bg-slate-50"
        data-testid={`diagnostic-rule-${rule.rule_name}`}
      >
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500">
            CARD-DRIVEN
          </Badge>
          <span className="font-mono text-xs text-slate-700">{rule.rule_name}</span>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          {rule.label} — exempt from §16 gate.
        </p>
      </div>
    );
  }
  const isZero = rule.final_count === 0;
  return (
    <div
      className={`border rounded p-3 ${
        isZero ? "border-rose-200 bg-rose-50/40" : "border-emerald-200 bg-emerald-50/30"
      }`}
      data-testid={`diagnostic-rule-${rule.rule_name}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {isZero ? (
            <AlertTriangle className="h-4 w-4 text-rose-600" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          )}
          <span className="font-mono text-xs text-slate-800">{rule.rule_name}</span>
        </div>
        <span
          className={`font-mono text-xs ${
            isZero ? "text-rose-700 font-semibold" : "text-emerald-700"
          }`}
          data-testid={`diagnostic-rule-${rule.rule_name}-final`}
        >
          final = {rule.final_count}
        </span>
      </div>
      <p className="text-xs text-slate-600 mt-1">{rule.label}</p>
      <table className="w-full text-[11px] mt-2 font-mono">
        <tbody>
          {(rule.predicates || []).map((p) => {
            const isBottle =
              rule.bottleneck && p.predicate === rule.bottleneck.predicate;
            return (
              <tr key={p.predicate}>
                <td className="text-slate-600 pr-2">
                  {p.predicate} = {Array.isArray(p.value) ? `[${p.value.join(", ")}]` : String(p.value)}
                </td>
                <td
                  className={`text-right ${
                    isBottle ? "text-rose-700 font-semibold" : "text-slate-700"
                  }`}
                >
                  {p.independent_count}
                  {isBottle ? " ← bottleneck" : ""}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {isZero && rule.suggested_fix && rule.suggested_fix !== "—" ? (
        <p
          className="text-xs text-rose-800 mt-2 bg-rose-100/60 rounded px-2 py-1"
          data-testid={`diagnostic-rule-${rule.rule_name}-fix`}
        >
          <strong>Suggested fix:</strong> {rule.suggested_fix}
        </p>
      ) : null}
    </div>
  );
}

function BulkTagSurface({ workspaceId }) {
  const [folder, setFolder] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [mode, setMode] = useState("merge");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(null);

  const parsedTags = tagsInput
    .split(/[,\n]/)
    .map((t) => t.trim())
    .filter(Boolean);

  const callBulkTag = useCallback(
    async (dryRun) => {
      setLoading(true);
      setError(null);
      if (dryRun) setApplied(null);
      try {
        const r = await authedFetch(
          `${API}/v2/workspaces/${workspaceId}/brand/assets/bulk-tag`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              folder_substring: folder,
              tags: parsedTags,
              mode,
              dry_run: dryRun,
            }),
          },
        );
        if (!r.ok) {
          let detail = `HTTP ${r.status}`;
          try {
            const j = await r.json();
            detail = j.detail || detail;
          } catch (_) {}
          throw new Error(detail);
        }
        const json = await r.json();
        if (dryRun) {
          setPreview(json);
        } else {
          setApplied(json);
          setPreview(null);
        }
      } catch (e) {
        setError(e.message || "Bulk-tag request failed");
      } finally {
        setLoading(false);
      }
    },
    [workspaceId, folder, parsedTags, mode],
  );

  const canPreview = folder.trim() && parsedTags.length > 0 && !loading;
  const canApply = preview && preview.matched_count > 0 && !loading;

  return (
    <div className="mt-4 pt-4 border-t border-slate-200 space-y-3" data-testid="bulk-tag-section">
      <div className="flex items-center gap-2">
        <Tag className="h-4 w-4 text-violet-700" />
        <span className="text-sm font-medium text-slate-800">
          Bulk tag by folder
        </span>
        <span className="text-xs text-slate-400 hidden sm:inline">
          — apply tags to every asset whose folder matches a substring
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div className="md:col-span-2">
          <label className="block text-[11px] text-slate-500 mb-0.5">
            Folder substring (case-insensitive)
          </label>
          <input
            type="text"
            value={folder}
            onChange={(e) => {
              setFolder(e.target.value);
              setPreview(null);
              setApplied(null);
            }}
            placeholder="e.g. Remi Profile Pics"
            className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-200"
            data-testid="bulk-tag-folder-input"
          />
        </div>
        <div>
          <label className="block text-[11px] text-slate-500 mb-0.5">Mode</label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded bg-white"
            data-testid="bulk-tag-mode-select"
          >
            <option value="merge">Merge (preserve existing tags)</option>
            <option value="replace">Replace (overwrite all tags)</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-[11px] text-slate-500 mb-0.5">
          Tags (comma- or newline-separated, no quoting needed)
        </label>
        <textarea
          value={tagsInput}
          onChange={(e) => {
            setTagsInput(e.target.value);
            setPreview(null);
            setApplied(null);
          }}
          rows={2}
          placeholder="portrait, remi, executive"
          className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-200 font-mono"
          data-testid="bulk-tag-tags-input"
        />
        {parsedTags.length > 0 ? (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {parsedTags.map((t) => (
              <span
                key={t}
                className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-violet-100 text-violet-700"
              >
                {t}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          variant="outline"
          onClick={() => callBulkTag(true)}
          disabled={!canPreview}
          data-testid="bulk-tag-preview-button"
        >
          <RotateCw className={`h-3.5 w-3.5 mr-1 ${loading && !applied ? "animate-spin" : ""}`} />
          Preview (count + sample)
        </Button>
        <Button
          size="sm"
          onClick={() => callBulkTag(false)}
          disabled={!canApply}
          data-testid="bulk-tag-apply-button"
          className="bg-violet-700 hover:bg-violet-800 text-white"
        >
          <Send className="h-3.5 w-3.5 mr-1" />
          {preview ? `Apply to ${preview.matched_count} assets` : "Apply"}
        </Button>
      </div>

      {error ? (
        <div
          className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3"
          data-testid="bulk-tag-error"
        >
          {error}
        </div>
      ) : null}

      {preview && !applied ? (
        <div
          className="text-xs border border-slate-200 rounded p-3 bg-slate-50"
          data-testid="bulk-tag-preview"
        >
          <p className="text-slate-700 mb-2">
            <strong>{preview.matched_count}</strong> asset
            {preview.matched_count === 1 ? "" : "s"} match the folder substring.{" "}
            {preview.matched_count > 0
              ? "Click Apply to write the tags + audit log entries."
              : "No assets match — refine the substring."}
          </p>
          {preview.preview && preview.preview.length > 0 ? (
            <details className="mt-1">
              <summary className="text-slate-500 cursor-pointer">
                Sample of first {preview.preview.length}
              </summary>
              <ul className="mt-1 space-y-0.5 font-mono text-[10px]">
                {preview.preview.map((p) => (
                  <li key={p.asset_id} className="text-slate-600">
                    {p.asset_id.slice(0, 8)} — {p.source_folder || "(no folder)"} · current
                    tags={" "}
                    {p.current_tags && p.current_tags.length > 0
                      ? p.current_tags.join(",")
                      : "(none)"}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}

      {applied ? (
        <div
          className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-3"
          data-testid="bulk-tag-applied"
        >
          <CheckCircle2 className="inline h-3.5 w-3.5 mr-1" />
          Applied — <strong>{applied.updated_count}</strong> of{" "}
          {applied.matched_count} asset
          {applied.matched_count === 1 ? "" : "s"} updated with{" "}
          {applied.applied_tags?.join(", ")} ({applied.mode} mode). Audit-logged.
        </div>
      ) : null}
    </div>
  );
}

export default function PoolAwarenessPanel({ workspaceId = DEFAULT_WS }) {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Full diagnostic state — separate from the pool-check above.
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const [diagnosticError, setDiagnosticError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authedFetch(
        `${API}/v2/workspaces/${workspaceId}/brand/art-director/pool-check`,
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      setData(json);
    } catch (e) {
      setError(e.message || "Could not load pool check");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  const runDiagnostic = useCallback(async () => {
    setDiagnosticLoading(true);
    setDiagnosticError(null);
    try {
      const r = await authedFetch(
        `${API}/v2/workspaces/${workspaceId}/brand/art-director/diagnostic?include_tag_vocabulary=true`,
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      setDiagnostic(json);
    } catch (e) {
      setDiagnosticError(e.message || "Could not run diagnostic");
    } finally {
      setDiagnosticLoading(false);
    }
  }, [workspaceId]);

  const downloadMarkdown = useCallback(async () => {
    try {
      const r = await authedFetch(
        `${API}/v2/workspaces/${workspaceId}/brand/art-director/diagnostic?include_tag_vocabulary=true&format=md`,
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const md = await r.text();
      const blob = new Blob([md], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `phase3_gate_diagnostic_${new Date().toISOString().slice(0, 10)}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setDiagnosticError(e.message || "Could not download markdown");
    }
  }, [workspaceId]);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !data && !loading) load();
  };

  const zeroCount = data?.rules_with_zero_candidates ?? 0;
  const gate = diagnostic?.gate;

  return (
    <div
      className="mb-4 border border-slate-200 bg-white rounded-lg overflow-hidden"
      data-testid="pool-awareness-panel"
    >
      <button
        type="button"
        onClick={toggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50"
        data-testid="pool-awareness-toggle"
      >
        <div className="flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-violet-600" />
          <span className="text-sm font-medium text-slate-800">
            Pool awareness · Art Director pre-flight
          </span>
          {data ? (
            <Badge
              variant="outline"
              className={`text-[10px] ${
                zeroCount > 0
                  ? "border-rose-200 text-rose-700 bg-rose-50"
                  : "border-emerald-200 text-emerald-700 bg-emerald-50"
              }`}
            >
              {zeroCount > 0
                ? `${zeroCount} of ${data.rule_count} rules at zero`
                : "All rules have candidates"}
            </Badge>
          ) : null}
          <span className="text-xs text-slate-400 hidden sm:inline">
            — diagnostic only, not customer-facing
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-slate-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-500" />
        )}
      </button>

      {expanded ? (
        <div className="border-t border-slate-200 p-4 space-y-3" data-testid="pool-awareness-body">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-slate-600">
              {data
                ? `Eligible pool: ${data.pool_total_eligible} active photos (excluding dedupe-dropped, zombies, unapproved stock).`
                : "Loading…"}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="pool-awareness-refresh"
            >
              <RotateCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          {loading && !data ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : error ? (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
              {error}
            </div>
          ) : data ? (
            <div className="border border-slate-200 rounded overflow-hidden">
              <table className="w-full text-xs" data-testid="pool-awareness-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 w-8"></th>
                    <th className="text-left px-3 py-2">Rule</th>
                    <th className="text-right px-3 py-2 w-24">Candidates</th>
                    <th className="text-right px-3 py-2 w-20">Confidence</th>
                    <th className="text-left px-3 py-2 hidden md:table-cell">Trigger</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rules.map((r) => (
                    <tr
                      key={r.name}
                      className={r.flagged ? "bg-rose-50/50" : ""}
                      data-testid={`pool-awareness-row-${r.name}`}
                    >
                      <td className="px-3 py-2 align-top">
                        {r.flagged ? (
                          <AlertTriangle
                            className="h-4 w-4 text-rose-600"
                            data-testid={`pool-awareness-flag-${r.name}`}
                          />
                        ) : (
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium text-slate-800">{r.label}</div>
                        <div className="font-mono text-[10px] text-slate-400">{r.name}</div>
                      </td>
                      <td className="px-3 py-2 align-top text-right font-mono">
                        {r.card_driven ? (
                          <span className="text-slate-400 italic">card-driven</span>
                        ) : (
                          <span
                            className={r.flagged ? "text-rose-700 font-semibold" : "text-slate-700"}
                          >
                            {r.candidate_count}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top text-right font-mono text-slate-500">
                        {r.confidence?.toFixed?.(2) || "—"}
                      </td>
                      <td className="px-3 py-2 align-top hidden md:table-cell">
                        <TriggerCell trigger={r.trigger} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {data && zeroCount > 0 ? (
            <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded p-3">
              <strong>What to do about red rows:</strong> a rule that returns zero candidates
              will fall through to the LLM or fail silently when the Art Director routes
              cards through it. Common
              fixes: (1) the trigger references tags or folder names that don't exist in
              the pool yet — adjust the rule, (2) the classifier isn't populating the
              required signal — check{" "}
              <code className="font-mono text-[11px] bg-amber-100 px-1 rounded">
                auto_classification.signals
              </code>{" "}
              coverage, (3) the rule was speculative and should be removed before
              implementation. Run the full diagnostic below to see which predicate is
              the bottleneck on each red rule.
            </div>
          ) : null}

          {/* ─── §16 day-0 gate — full per-rule diagnostic ─── */}
          <div
            className="mt-4 pt-4 border-t border-slate-200 space-y-3"
            data-testid="diagnostic-section"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Stethoscope className="h-4 w-4 text-violet-700" />
                <span className="text-sm font-medium text-slate-800">
                  §16 day-0 gate — full diagnostic
                </span>
                {gate ? (
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      gate.passing
                        ? "border-emerald-200 text-emerald-700 bg-emerald-50"
                        : "border-rose-200 text-rose-700 bg-rose-50"
                    }`}
                    data-testid="diagnostic-gate-badge"
                  >
                    {gate.passing
                      ? `PASS — ${gate.non_zero_count}/${gate.pool_driven_rule_count} rules non-zero`
                      : `FAIL — ${gate.zero_count} of ${gate.pool_driven_rule_count} rules at zero`}
                  </Badge>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={runDiagnostic}
                  disabled={diagnosticLoading}
                  data-testid="diagnostic-run-button"
                >
                  <RotateCw
                    className={`h-3.5 w-3.5 mr-1 ${diagnosticLoading ? "animate-spin" : ""}`}
                  />
                  {diagnostic ? "Re-run diagnostic" : "Run full diagnostic"}
                </Button>
                {diagnostic ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadMarkdown}
                    data-testid="diagnostic-download-md"
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    Download markdown
                  </Button>
                ) : null}
              </div>
            </div>

            {diagnosticLoading && !diagnostic ? (
              <div className="space-y-2">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : diagnosticError ? (
              <div
                className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3"
                data-testid="diagnostic-error"
              >
                {diagnosticError}
              </div>
            ) : diagnostic ? (
              <div className="space-y-3" data-testid="diagnostic-results">
                <p className="text-xs text-slate-500">
                  Decomposes each pool-driven rule into its independent predicates.
                  The lowest count is the bottleneck — fix that and the AND-of-all
                  count climbs.{" "}
                  <span className="text-slate-400">
                    Pool total: {diagnostic.pool_total}.
                  </span>
                </p>
                {diagnostic.rules.map((r) => (
                  <DiagnosticRuleBlock key={r.rule_name} rule={r} />
                ))}
                {diagnostic.tag_vocabulary ? (
                  <details
                    className="border border-slate-200 rounded p-3"
                    data-testid="diagnostic-tag-vocabulary"
                  >
                    <summary className="text-xs font-medium text-slate-700 cursor-pointer">
                      Top tag vocabulary in eligible pool ({diagnostic.tag_vocabulary.length})
                    </summary>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {diagnostic.tag_vocabulary.map((t) => (
                        <span
                          key={t.tag}
                          className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-700"
                        >
                          {t.tag} <span className="text-slate-400">·{t.count}</span>
                        </span>
                      ))}
                    </div>
                  </details>
                ) : null}
              </div>
            ) : (
              <p className="text-xs text-slate-500">
                Click <em>Run full diagnostic</em> to see per-predicate counts and
                bottleneck identification for each rule. Use the markdown download
                to paste into spec §16.5.
              </p>
            )}
          </div>

          {/* ─── Bulk-tag-by-folder affordance ─── */}
          <BulkTagSurface workspaceId={workspaceId} />
        </div>
      ) : null}
    </div>
  );
}
